import type { IPanoramaDetails, IPointOfInterest, IScan, ScanProvider } from '@wr/scan-renderer';
import {
  BuildingScanFloorService,
  BuildingScanService,
  BuildingScanViewerDetailDto,
  PointOfInterestDetailDto,
  PointOfInterestEditDto,
  PointOfInterestService,
  ShareLinkDtoObject,
  ShareLinkService,
} from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { DialogService } from '@services/dialog-service';
import { EventEmitter, Signal } from '@angular/core';
import { CurrentUserService } from '@services/current-user.service';

export class ScanViewerAdapter implements ScanProvider {
  constructor(
    readonly scan: BuildingScanViewerDetailDto,
    readonly pointOfInterestService: PointOfInterestService,
    readonly dialogService: DialogService,
    readonly buildingScanService: BuildingScanService,
    readonly floorService: BuildingScanFloorService,
    readonly isReadonly: boolean,
    readonly shareLinkService: ShareLinkService,
    readonly shareLink: Signal<ShareLinkDtoObject> | null,
    readonly currentUserService: CurrentUserService,
  ) {
  }

  async loadScan(): Promise<IScan> {
    return {
      id: this.scan.id,
      panoramas: this.scan.panoramas.map(panorama => {
        const { id, transform: pose, floorId } = panorama;

        return { id, pose, floorId };
      }),
      pointsOfInterest: this.scan.pointsOfInterest.map(it => ({ ...it, iconUrl: it.icon?.presignedUrl  })),
      floors: this.scan.floors,
    };
  }

  async getPanoramaDetails(scanId: string): Promise<IPanoramaDetails> {
    const {
      id,
      transform: pose,
      imageUrl,
      previewUrl,
      chunks,
      chunksX,
      chunksY
    } = await firstValueFrom(this.buildingScanService.getBuildingScanPanoramaById(scanId));
    return {
      id,
      pose,
      imageUrl,
      previewUrl,
      chunks,
      chunksX,
      chunksY
    };
  }

  convertPointOfInterest(entity: PointOfInterestDetailDto): IPointOfInterest {
    return {
      id: entity.id,
      name: entity.name,
      position: entity.position,
      panoramaId: entity.panoramaId,
      buildingEquipmentId: entity.buildingEquipment?.id,
      maintenanceTimerId: entity.maintenanceTimer?.id,
      iconUrl: entity.icon?.presignedUrl,
      color: entity.color
    };
  }

  pointOfInterestedChanged = new EventEmitter<PointOfInterestDetailDto>();

  async editPointOfInterest(id: string, options: PointOfInterestEditDto) {
    const result = await firstValueFrom(this.pointOfInterestService.update(id, options));

    this.pointOfInterestedChanged.emit(result);

    return this.convertPointOfInterest(result);
  }

  async getPOIDetails(id: string) {
    const shareLink = this.shareLink?.();
    if (shareLink) {
      return await firstValueFrom(this.shareLinkService.getPointOfInterestById(shareLink.id!, id));
    }

    return await firstValueFrom(this.pointOfInterestService.getById(id));
  }

  async deletePointOfInterest(id: string): Promise<boolean> {
    try {
      if (await this.dialogService.showDeleteDialog(
        'Point of Interest löschen',
        'Bist du sicher, dass du diesen Point of Interest löschen willst?',
      )) {
        await firstValueFrom(this.pointOfInterestService.deletePointOfInterest(id));
        return true;
      }
    } catch (e) {
      console.error(e);
    }

    return false;
  }

  editFloor(id: string, options: { name: string }) {
    return firstValueFrom(this.floorService.update(this.scan.id, id, options));
  }
}
